import { Link } from "react-router-dom";
import { Clock, MapPin, Package, ShieldCheck, Truck, BadgeCheck } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";

const AREA_SLUGS = {
  "Wharf": "weed-delivery-wharf",
  "Shaw": "weed-delivery-shaw",
  "NoMa": "weed-delivery-noma",
  "Navy Yard": "weed-delivery-navy-yard",
  "Georgetown": "weed-delivery-georgetown",
  "Adams Morgan": "weed-delivery-adams-morgan",
  "Foggy Bottom": "weed-delivery-foggy-bottom",
  "U Street Corridor": "weed-delivery-u-street-corridor",
  "Capitol Hill": "weed-delivery-capitol-hill",
  "Dupont Circle": "weed-delivery-dupont-circle",
  "Downtown / National Mall": "weed-delivery-downtown-dc",
  "Northwest DC": "weed-delivery-northwest-dc",
  "H Street NE": "weed-delivery-h-street-ne",
  "Columbia Heights": "weed-delivery-columbia-heights",
  "Southwest Waterfront": "weed-delivery-southwest-waterfront",
  "Brookland": "weed-delivery-brookland",
};

const AREAS = Object.keys(AREA_SLUGS);

export default function Delivery() {
  return (
    <main data-testid="delivery-page">
      <Seo
        title="Cannabis Delivery in Washington, DC | Farmacy District DC"
        description="Fast, discreet cannabis delivery throughout Washington, DC — typically 30–90 minutes. Serving all major DC neighborhoods. Initiative 71 compliant, adults 21+."
      />
      <section className="relative bg-brand-ink py-24 text-white grain sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-lime">Delivery & Pickup</p>
            <h1 className="mt-4 max-w-2xl font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl">
              Fast, Reliable Cannabis Delivery in D.C.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-300">
              Order from the full Farmacy District menu and get it delivered fast and discreet — anywhere in the
              District, typically within 30–90 minutes.
            </p>
            <Link
              to="/shop"
              data-testid="delivery-order-now-cta"
              className="mt-8 inline-block rounded-full bg-brand-lime px-9 py-4 text-sm font-bold tracking-wide text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover"
            >
              ORDER NOW
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {[
            { icon: Clock, title: "30–90 Minute Windows", text: "Most deliveries arrive within 30–90 minutes, with clear communication at every step." },
            { icon: ShieldCheck, title: "Always Discreet", text: "Unmarked, professional delivery. Your privacy is protected from checkout to doorstep." },
            { icon: MapPin, title: "All of DC Covered", text: "Every quadrant of the District — homes, hotels, Airbnbs and short-term stays." },
            { icon: BadgeCheck, title: "21+ Verified", text: "Quick age verification at checkout and valid ID on delivery. I-71 compliant gifting." },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full border border-neutral-200 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-brand-ink hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]" data-testid={`delivery-feature-${i}`}>
                <f.icon className="h-7 w-7 text-brand-red" />
                <h2 className="mt-4 font-display text-lg font-bold tracking-tight">{f.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-neutral-500">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-sand py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden">
              <img
                src="https://irp.cdn-website.com/7c67d2fe/dms3rep/multi/opt/pexels-photo-14210477-1435h.jpeg"
                alt="Washington Monument at night, Washington DC"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Neighborhoods We Serve</h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-600">
              We deliver to all major DC neighborhoods, residential areas, hotels and short-term stays — in compliance
              with local cannabis laws. We deliver within Washington, DC only.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {AREAS.map((a) => (
                <Link
                  key={a}
                  to={`/${AREA_SLUGS[a]}`}
                  data-testid={`delivery-area-${AREA_SLUGS[a]}`}
                  className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-semibold transition-colors hover:border-brand-ink hover:bg-brand-ink hover:text-white"
                >
                  {a}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <Reveal>
          <div className="grid gap-10 lg:grid-cols-2">
            <div className="border border-neutral-200 p-8 sm:p-10">
              <Truck className="h-7 w-7 text-brand-red" />
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight">How Delivery Works</h2>
              <ol className="mt-6 space-y-4">
                {[
                  "Browse the menu and add products to your cart.",
                  "Complete checkout with quick 21+ age verification.",
                  "Our team confirms your order and dispatches a driver.",
                  "Meet your driver with valid ID — delivery in about 30–90 minutes.",
                ].map((s, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-ink text-xs font-bold text-white">{i + 1}</span>
                    <p className="text-sm leading-relaxed text-neutral-600">{s}</p>
                  </li>
                ))}
              </ol>
            </div>
            <div className="border border-neutral-200 p-8 sm:p-10" data-testid="pickup-info">
              <Package className="h-7 w-7 text-brand-red" />
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight">Pickup</h2>
              <p className="mt-4 text-sm leading-relaxed text-neutral-600">
                Prefer to grab your order yourself? Pickup is available by arrangement. Place your order online and
                our team will coordinate a fast, easy pickup.
              </p>
              <div className="mt-6 space-y-2 text-sm">
                <p className="flex items-center gap-2.5 text-neutral-700"><MapPin className="h-4 w-4 text-brand-red" /> 1100 New Jersey Ave SE, Washington, DC</p>
                <p className="flex items-center gap-2.5 text-neutral-700"><Clock className="h-4 w-4 text-brand-red" /> Coordinated at order confirmation</p>
              </div>
              <a href="tel:+12029134523" className="mt-7 inline-block rounded-full border border-brand-ink px-7 py-3 text-sm font-bold transition-colors hover:bg-brand-ink hover:text-white" data-testid="delivery-call-cta">
                CALL (202) 913-4523
              </a>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-14 bg-brand-ink p-10 text-center text-white grain sm:p-14">
            <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Ready When You Are.</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
              Premium flower, edibles, vapes and concentrates — delivered fast and discreet across DC.
            </p>
            <Link to="/shop" data-testid="delivery-bottom-cta" className="mt-7 inline-block rounded-full bg-brand-lime px-9 py-4 text-sm font-bold text-brand-ink transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-limehover">
              ORDER NOW
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
